import type {
  AdministrativeArea,
  City,
  ContactPoint,
  GeoCoordinates,
  ImageObject,
  LocalBusiness,
  Offer,
  OfferCatalog,
  OpeningHoursSpecification,
  Organization,
  PostalAddress,
  Service,
  WithContext,
} from "schema-dts";
import { mapOpeningHours, type OpeningHoursInput } from "./helpers";

export type LocalBusinessSchemaOptions = {
  type: string;
  id: string; // canonical @id, e.g. https://url.com/#org
  url: string; // canonical organization URL
  name: string; // Legal Name
  alternateName?: string;
  description?: string;
  slogan?: string;
  logo: string | ImageObject;
  images?: (string | ImageObject)[];
  telephone?: string;
  email?: string;
  priceRange?: "$" | "$$" | "$$$" | "$$$$";
  foundingDate?: string;

  // Brand/Identity
  sameAs?: string[];

  // Geography/Presence
  address: {
    streetAddress: string;
    addressLocality: string; // City
    addressRegion: string; // State code
    postalCode: string;
    addressCountry?: string; // defaults "US"
    geo?: { latitude: number; longitude: number };
    hasMap?: string; // Google Maps URL
  };

  // Optional: additional offices as departments of this org
  departments?: Array<{
    id: string;
    name?: string;
    url?: string;
    address: LocalBusinessSchemaOptions["address"];
    openingHours?: OpeningHoursInput[];
    telephone?: string;
    email?: string;
  }>;

  // Service area
  areaServed?: {
    cities?: string[];
    states?: string[];
  };

  // Hours
  openingHours?: OpeningHoursInput[];

  // Languages
  availableLanguage?: string[]; // ["English","Spanish"]

  // Contacts
  contactPoints?: Array<{
    contactType: string; // "customer service"
    telephone?: string;
    email?: string;
    areaServed?: string | string[];
    availableLanguage?: string | string[];
  }>;

  // Knowledge tags (helps Google understand topical relevance)
  knowsAbout?: string[];

  // Offers / catalog
  catalog?: CatalogInput;
};

export type CatalogInput = {
  name?: string;
  categories: Array<{
    name: string;
    items: Array<{
      name: string;
      url?: string;
      description?: string;
    }>;
  }>;
};

export function buildLocalBusinessSchema({
  type,
  url,
  name,
  alternateName,
  description,
  slogan,
  logo,
  images,
  telephone,
  email,
  priceRange,
  foundingDate,
  sameAs,
  address,
  departments,
  areaServed,
  openingHours,
  availableLanguage,
  contactPoints,
  knowsAbout,
  catalog,
  ...rest
}: LocalBusinessSchemaOptions): WithContext<LocalBusiness> {
  const addr: PostalAddress = {
    "@type": "PostalAddress",
    streetAddress: address.streetAddress,
    addressLocality: address.addressLocality,
    addressRegion: address.addressRegion,
    postalCode: address.postalCode,
    addressCountry: address.addressCountry ?? "US",
  };

  const geo: GeoCoordinates | undefined = address.geo
    ? {
        "@type": "GeoCoordinates",
        latitude: address.geo.latitude,
        longitude: address.geo.longitude,
      }
    : undefined;

  const openingHoursSpec: OpeningHoursSpecification[] | undefined = openingHours
    ? mapOpeningHours(openingHours)
    : undefined;

  const areaServedThings: (City | AdministrativeArea)[] | undefined = areaServed
    ? [
        ...(areaServed.cities?.map(
          (c): City => ({ "@type": "City", name: c }),
        ) ?? []),
        ...(areaServed.states?.map(
          (s): AdministrativeArea => ({ "@type": "State", name: s }),
        ) ?? []),
      ]
    : undefined;

  const contactPointList: ContactPoint[] | undefined = contactPoints?.map(
    cp => ({
      "@type": "ContactPoint",
      contactType: cp.contactType,
      telephone: cp.telephone,
      email: cp.email,
      areaServed: cp.areaServed,
      availableLanguage: cp.availableLanguage,
    }),
  );

  const hasOfferCatalog: OfferCatalog | undefined = catalog
    ? {
        "@type": "OfferCatalog",
        name: catalog.name ?? "",
        itemListElement: catalog.categories.map(cat => ({
          "@type": "OfferCatalog",
          name: cat.name,
          itemListElement: cat.items.map<Offer>(it => ({
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: it.name,
              url: it.url,
              description: it.description,
            } satisfies Service,
          })),
        })),
      }
    : undefined;

  const departmentNodes: Organization[] | undefined = departments?.map(dept => {
    const deptAddr: PostalAddress = {
      "@type": "PostalAddress",
      streetAddress: dept.address.streetAddress,
      addressLocality: dept.address.addressLocality,
      addressRegion: dept.address.addressRegion,
      postalCode: dept.address.postalCode,
      addressCountry: dept.address.addressCountry ?? "US",
    };

    const deptGeo: GeoCoordinates | undefined = dept.address.geo
      ? {
          "@type": "GeoCoordinates",
          latitude: dept.address.geo.latitude,
          longitude: dept.address.geo.longitude,
        }
      : undefined;

    return {
      "@type": "AccountingService",
      "@id": dept.id,
      name: dept.name ?? name,
      url: dept.url ?? url,
      address: deptAddr,
      geo: deptGeo,
      hasMap: dept.address.hasMap,
      openingHoursSpecification: dept.openingHours
        ? mapOpeningHours(dept.openingHours)
        : undefined,
      telephone: dept.telephone,
      email: dept.email,
    } as Organization;
  });

  return {
    "@context": "https://schema.org",
    "@type": type as any,
    "@id": `${url}/#localbusiness`,
    url,
    name,
    alternateName,
    description,
    slogan,
    logo,
    image: images,
    telephone,
    email,
    priceRange,
    foundingDate,
    sameAs,
    address: addr,
    geo,
    hasMap: address.hasMap,
    areaServed: areaServedThings,
    openingHoursSpecification: openingHoursSpec,
    contactPoint: contactPointList,
    knowsAbout,
    hasOfferCatalog,
    department: departmentNodes,
    ...rest,
  };
}
